import React from 'react';
import dayjs from 'dayjs';
import Helpers from '@Apis/helpers';
import { QUANTITY, DEFAULT_CUR_ROUND_OFF } from '@Apis/constants';
import HideValue from '../../../Common/RestrictedAccess/HideValue';

const ListScreenKeys = ({ documentData, MONEY, priceMasking }) => {
  const record = documentData;

  const { isDataMaskingPolicyEnable, isHideCostPrice, isHideSellingPrice } = priceMasking;

  let qty = 0;
  for (let i = 0; i < record?.reserved_quantities?.length; i++) {
    qty += record?.reserved_quantities?.[i]?.reserved_qty;
  }

  const expiryMoment = dayjs(dayjs(record?.expiry_date).format('YYYY-MM-DD'));
  let shelfLife = 0;
  if (dayjs().isSameOrBefore(expiryMoment)) {
    shelfLife = expiryMoment.diff(dayjs(dayjs().format('YYYY-MM-DD')), 'days');
  }
  // eslint-disable-next-line prefer-const
  let shelfLifePct = Math.round(((shelfLife / record?.expiry_days) * 100));

  const toProperCase = (text) => {
    return text
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  let infoArray = [
    {
      label: 'SKU#',
      value: `${record?.internal_sku_code}`,
    },
    {
      label: 'INTERNAL SKU#',
      value: `${record?.ref_product_code || '-'}`,
    },
    {
      label: 'PRODUCT CATEGORY',
      value: `${record?.product_category_info ? (
        `.../${record?.product_category_info?.product_category_name}`
      ) : (
        '-'
      )}`,
    },
    {
      label: 'ON HAND',
      value: `${QUANTITY(record?.available_qty, record?.uom_info?.precision)} ${record?.uom_info?.uqc?.toProperCase() || ''}`,
    },
    {
      label: 'RESERVED',
      value: `${QUANTITY(record?.reserved_quantity, record?.uom_info?.precision)} ${record?.uom_info?.uqc?.toProperCase() || ''}`,
    },
    {
      label: 'AVAILABLE STOCK',
      value: `${QUANTITY(record?.available_qty - qty, record?.uom_info?.precision)} ${record?.uom_info?.uqc?.toProperCase() || ''}`,
    },
    {
      label: 'BATCH #',
      value: record?.custom_batch_number ? record.custom_batch_number : record?.batch_number,
    },
    {
      label: 'LOT #',
      value: record?.lot_number ? record?.lot_number : '-',
    },
    {
      label: 'VENDOR',
      value: record?.seller_name,
    },
    {
      label: 'AR NUMBER',
      value: record?.ar_number ? record?.ar_number : '-',
    },
    {
      label: 'QUALITY',
      value: `${record?.pending_quality_check_info?.quality_check_id ? 'Pending for QC' : '-'}`
      ,
    },
    {
      label: 'EXPIRY',
      value: shelfLife !== 0 ? (record?.expiry_date === '2099-01-01' ? 'No expiry' : `${shelfLife} Days (${shelfLifePct > 100 ? 100 : shelfLifePct}%)`) : `Expired on ${dayjs(record?.expiry_date).format('DD MMM, YY')}`,
    },
    {
      label: 'WAREHOUSE BIN',
      value: record?.inventory_location_path,
    },
    {
      label: 'BARCODE',
      value: record?.batch_barcode,
    },
    {
      label: 'CATEGORY',
      value: `${record?.product_category ? "record?.product_category" : "-"} ${record?.is_marketplace_product ? "Marketplace" : "-"}`,
    },
    {
      label: 'REORDER LEVEL',
      value: `${QUANTITY(record?.threshold_qty, record?.uom_info?.precision)} ${record?.uom_info?.uqc?.toProperCase() || ''}`,
    },
    {
      label: 'TAX',
      value: `${record?.tax_info?.tax_value}%`,
    },
    {
      label: 'CREATED BY',
      value: `${dayjs(record?.created_at).format('DD/MM/YYYY')} ${record?.created_by_info ? `${record?.created_by_info.first_name} ${record?.created_by_info.last_name}` : ''}`,
    },
    {
      label: 'BATCH INWARD DATE',
      value: record?.batch_inward_date ? dayjs(record?.batch_inward_date).format('DD/MM/YYYY') : '-',
    },
    {
      label: 'ROLL NUMBER #',
      value: record?.roll_no ? record?.roll_no : '-',
    },
    {
      label: 'COST PRICE',
      value: (isDataMaskingPolicyEnable && isHideCostPrice) ? <HideValue showPopOver popOverMessage="You don't have access to view cost price" /> : MONEY(record?.cost_price)
    },
    {
      label: 'FREIGHT COST',
      value: MONEY(record?.freight_cost),
    },
    {
      label: 'OTHER COST',
      value: MONEY(record?.other_cost),
    },
    {
      label: 'LANDED COST',
      value: (isDataMaskingPolicyEnable && isHideCostPrice) ? <HideValue showPopOver popOverMessage="You don't have access to view landed cost" /> : MONEY(record?.landed_cost)
    },
    {
      label: 'SELLING PRICE',
      value: (isDataMaskingPolicyEnable && isHideSellingPrice) ? <HideValue showPopOver popOverMessage="You don't have access to view selling price" /> : MONEY(record?.selling_price)
    },
    {
      label: 'MRP',
      value: MONEY(record?.mrp),
    },
    {
      label: 'MARGIN',
      value: record?.margin ? `${parseFloat(Number(record?.margin).toFixed(DEFAULT_CUR_ROUND_OFF))}%` : '-',
    },
    {
      label: 'QC NUMBER',
      value: record?.pending_quality_check_info?.qc_number || '-',
    },
    {
      label: 'LAST QC DATE',
      value: record?.last_qc_date ? dayjs(record?.last_qc_date).format('DD/MM/YYYY') : '-',
    },
    {
      label: 'NEXT QC DATE',
      value: record?.next_qc_date ? dayjs(record?.next_qc_date).format('DD/MM/YYYY') : '-',
    },
    {
      label: 'BRAND',
      value: record?.brand ? record?.brand : '-',
    },
    {
      label: 'MFG BATCH NUMBER #',
      value: record?.mfg_batch_no ? record?.mfg_batch_no : '-',
    },
    {
      label: 'MANUFACTURING DATE',
      value: record?.manufacturing_date ? Helpers.batchDateFormatter(record?.manufacturing_date, record?.manufacturing_date_format) : '-',
    },
  ];
  return infoArray.map(item => ({
    ...item,
    label: toProperCase(item.label),
  }))
};

export default ListScreenKeys;